// ── Reddit Subreddits Data ──────────────────────────────────────

export interface SubredditEntry {
  name: string;
  category: "startup" | "marketing" | "tech" | "general" | "seo";
  allowsPromo: boolean;
  notes?: string;
  strategy?: string;
  format?: string[];
  optimization?: string[];
}

export const SUBREDDIT_STRATEGIES: SubredditEntry[] = [
  {
    name: "r/InternetIsBeautiful",
    category: "general",
    allowsPromo: false,
    notes: "Ideal for curious or novel apps. Can generate a lot of traffic quickly.",
    strategy: "Only link + powerful title. Direct promotion is not allowed.",
    format: [
      "\"I made a site to [clear benefit for the user]\"",
      "Create several versions of the title",
      "Test which one is more memorable before publishing",
    ],
  },
  {
    name: "r/dataisbeautiful",
    category: "general",
    allowsPromo: false,
    notes: "Focused on data visualization. Ideal if your product shows interesting graphics or data.",
  },
  {
    name: "r/SideProject",
    category: "startup",
    allowsPromo: true,
    notes: "Community of creators and entrepreneurs. Allows promotion. Good place to receive feedback.",
    format: [
      "Publish a demo video",
      "Explain what the product does, why it exists and how you built it",
      "Do not include the link in the title",
      "Add the link in the first comment",
      "Reply to all comments",
    ],
  },
  {
    name: "r/Entrepreneur",
    category: "startup",
    allowsPromo: false,
    notes: "Does not allow direct promotion.",
    strategy: "Share an interesting story of entrepreneurship. If the product is relevant to the conversation, mention it naturally. Can generate traffic if the story connects.",
  },
  {
    name: "r/Startups",
    category: "startup",
    allowsPromo: false,
    notes: "Large startup community. Focus on sharing lessons learned and asking for feedback.",
  },
  {
    name: "r/advancedentrepreneur",
    category: "startup",
    allowsPromo: false,
    notes: "More experienced entrepreneurs. Share in-depth insights and business strategy.",
  },
  {
    name: "r/saas",
    category: "startup",
    allowsPromo: true,
    notes: "SaaS-focused community. Good for sharing metrics, growth stories, and product updates.",
  },
  {
    name: "r/AlphaandBetausers",
    category: "startup",
    allowsPromo: true,
    notes: "Specifically for finding early testers. Post your product and ask for feedback.",
  },
  {
    name: "r/MadeThis",
    category: "startup",
    allowsPromo: true,
    notes: "Show what you've built. Visual posts perform best.",
  },
  {
    name: "r/Plugyourproduct",
    category: "startup",
    allowsPromo: true,
    notes: "Explicitly for product promotion. Low barrier to entry.",
  },
  {
    name: "r/ProductMgmt",
    category: "startup",
    allowsPromo: false,
    notes: "Product management community. Share insights about product strategy, not direct promotion.",
  },
  {
    name: "r/RoastMyStartup",
    category: "startup",
    allowsPromo: true,
    notes: "Get honest feedback on your startup. Be prepared for direct criticism.",
  },
  {
    name: "r/smallbusiness",
    category: "startup",
    allowsPromo: false,
    notes: "Small business owners. Share relevant advice and tools that help SMBs.",
  },
  {
    name: "r/startup",
    category: "startup",
    allowsPromo: true,
    notes: "General startup discussions. Good for sharing progress and milestones.",
  },
  {
    name: "r/startups_promotion",
    category: "startup",
    allowsPromo: true,
    notes: "Dedicated to startup promotion. Post freely.",
  },
  {
    name: "r/sweatystartup",
    category: "startup",
    allowsPromo: false,
    notes: "Focused on service-based and hands-on businesses.",
  },
  {
    name: "r/zerotoSaas",
    category: "startup",
    allowsPromo: true,
    notes: "Building SaaS from scratch. Share your journey and progress.",
  },
  {
    name: "r/thesidehustle",
    category: "startup",
    allowsPromo: true,
    notes: "Side project and hustle community. Good for sharing how you built something on the side.",
  },
  {
    name: "r/EntrepreneurRideAlong",
    category: "startup",
    allowsPromo: true,
    notes: "Follow-along entrepreneurship stories. Document your journey publicly.",
  },
  {
    name: "r/Startup_Ideas",
    category: "startup",
    allowsPromo: true,
    notes: "Share and validate startup ideas. Get early signal on your concept.",
  },
  {
    name: "r/startup_resources",
    category: "startup",
    allowsPromo: true,
    notes: "Share tools and resources for startups. Your product can be a resource.",
  },
  {
    name: "r/SEO",
    category: "seo",
    allowsPromo: false,
    notes: "Main SEO community. Share strategies and case studies.",
  },
  {
    name: "r/bigseo",
    category: "seo",
    allowsPromo: false,
    notes: "Advanced SEO discussions. More technical and scale-oriented.",
  },
  {
    name: "r/linkbuilding",
    category: "seo",
    allowsPromo: false,
    notes: "Link building strategies and tactics.",
  },
  {
    name: "r/seogrowth",
    category: "seo",
    allowsPromo: false,
    notes: "SEO growth strategies. Share organic growth case studies.",
  },
  {
    name: "r/socialmedia",
    category: "marketing",
    allowsPromo: false,
    notes: "Social media marketing discussions. Share strategies, not products.",
  },
  {
    name: "r/GrowthHacking",
    category: "marketing",
    allowsPromo: false,
    notes: "Growth strategies and tactics. Share experiments and results.",
  },
  {
    name: "r/marketing",
    category: "marketing",
    allowsPromo: false,
    notes: "General marketing community. Share insights and ask questions.",
  },
  {
    name: "r/PPC",
    category: "marketing",
    allowsPromo: false,
    notes: "Pay-per-click advertising. Useful for learning ad strategies.",
  },
  {
    name: "r/indiebiz",
    category: "startup",
    allowsPromo: true,
    notes: "Independent business owners. Share your indie business journey.",
  },
  {
    name: "r/growmybusiness",
    category: "marketing",
    allowsPromo: true,
    notes: "Business growth focused. Share strategies and get advice.",
  },
  {
    name: "r/productivity",
    category: "general",
    allowsPromo: false,
    notes: "Productivity tools and tips. Mention your product only if genuinely relevant.",
  },
  {
    name: "r/Webdev",
    category: "tech",
    allowsPromo: false,
    notes: "Web development community. Share technical posts about building your product.",
  },
  {
    name: "r/programming",
    category: "tech",
    allowsPromo: false,
    notes: "Large programming community. Share open-source work or technical writeups.",
  },
  {
    name: "r/Webdesign",
    category: "tech",
    allowsPromo: false,
    notes: "Web design community. Share design-related content about your product.",
  },
  {
    name: "r/design_critiques",
    category: "tech",
    allowsPromo: true,
    notes: "Get design feedback. Post screenshots and ask for UI/UX critique.",
  },
  {
    name: "r/Coupons",
    category: "general",
    allowsPromo: true,
    notes: "Share deals and discounts. Good for launch promos.",
  },
  {
    name: "r/LadyBusiness",
    category: "startup",
    allowsPromo: true,
    notes: "Women entrepreneurs community. Share your business story.",
  },
  {
    name: "r/AskReddit",
    category: "general",
    allowsPromo: false,
    notes: "Massive community. Cannot promote directly but great for brand awareness through comments.",
  },
  {
    name: "r/TodayILearned",
    category: "general",
    allowsPromo: false,
    notes: "Share interesting facts. If your product reveals something surprising, share that angle.",
  },
  {
    name: "r/WantToLearn",
    category: "general",
    allowsPromo: false,
    notes: "People wanting to learn skills. Your product can be recommended as a tool.",
  },
  {
    name: "r/IndieDev",
    category: "tech",
    allowsPromo: true,
    notes: "Independent developers. Share your development journey and product.",
  },
  {
    name: "r/B2B",
    category: "marketing",
    allowsPromo: true,
    notes: "B2B-focused community. Good for SaaS growth conversations.",
  },
  {
    name: "r/ecommerce",
    category: "marketing",
    allowsPromo: false,
    notes: "E-commerce discussions. Share tools and strategies relevant to online sellers.",
  },
  {
    name: "r/freelance",
    category: "startup",
    allowsPromo: false,
    notes: "Freelancer community. Share tools that help freelancers.",
  },
  {
    name: "r/business",
    category: "startup",
    allowsPromo: false,
    notes: "General business discussions. Share insights, not promotions.",
  },
];

export const SUBREDDIT_CATEGORIES: Record<SubredditEntry["category"], string> = {
  startup: "Startups & Entrepreneurship",
  marketing: "Marketing & Growth",
  tech: "Tech & Development",
  general: "General & High-Traffic",
  seo: "SEO & Organic",
};
